import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import useMountedState from 'react-use/lib/useMountedState';

import * as actions from '../../../permissions/store/actions';
import { ROLE_SUPER_ADMIN } from '../../../permissions/store/constants';
import { useComponentDidMount } from '../../hooks';
import { SelectInput } from '../forms';
import { RoleInterface, RoleRequestInterface, UserRoleInterface } from 'modules/auth/store/roleApi';

interface RoleSelectorProps {
  name?: string
  placeholder?: string
  hideSuperAdmin?: boolean
  roles?: RoleInterface[]
  userRoles?: UserRoleInterface[]
  getRoles: (args?: RoleRequestInterface | null) => Promise<{ payload: RoleInterface[] }>; 
  filterPartnerId?: string
  showAllRoles?: boolean
  showDeleted?: boolean
}

const RoleSelector = (props:RoleSelectorProps) => {
  const isMounted = useMountedState();
  const {
    name = 'roleId',
    hideSuperAdmin = true,
    roles,
    userRoles,
    getRoles,
    filterPartnerId,
    showAllRoles = false,
    showDeleted = false,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);
  const [accessibleRoles, setAccessibleRoles] = useState([]);

  const isSuperAdmin = userRoles.find(userRole => {
    return (ROLE_SUPER_ADMIN.includes(userRole.roleId)) && userRole.available;
  });

  useComponentDidMount(() => {
    setLoading(true);

    getRoles().finally(() => isMounted && setLoading(false));
  });

  const filterSuperAdmin = () => {
    return roles.filter(role => {
      if (!hideSuperAdmin) {
        return role;
      }

      // роль суперадмина можно раздавать, если сам являешься суперадмином
      // @see https://avtoapp.atlassian.net/browse/AV-2328

      return isSuperAdmin || (!ROLE_SUPER_ADMIN.includes(role.id));
    });
  };

  useEffect(() => {
    if (showAllRoles) {
      setAccessibleRoles(filterSuperAdmin());
      return () => setAccessibleRoles([]);
    }
    if (!((filterPartnerId ?? '') === '')) setAccessibleRoles(filterSuperAdmin().filter(role => role.partnerId === filterPartnerId));
    else setAccessibleRoles(filterSuperAdmin().filter(role => !role.partnerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => setAccessibleRoles([]);
  }, [hideSuperAdmin, userRoles, roles, isSuperAdmin, filterPartnerId]);

  const filledRoles = accessibleRoles
    .filter(role => showDeleted || !role.deletedDate )
    .map(v => (v.partnerName ? {
      ...v,
      name: `${ v.name } (${ v.partnerName })`,
    } : v));

  return (
    <SelectInput
      { ...rest }
      name={ name }
      data={ filledRoles }
      dataTitle='name'
      dataValue='id'
      loading={ loading }
      sort
    />
  );
};

const mapStateToProps = (state) => {
  return {
    roles: state.permissions?.roles,
    userRoles: state.permissions?.userRoles,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getRoles: payload => dispatch(actions.getRoles(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RoleSelector);
